import React, { MouseEventHandler, ReactNode } from "react";
import styled from "@emotion/styled";

import { Icon, IconName } from "../common/Icon";

const Text = styled.span`
    color: var(--formant-color-primary-silver);
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    flex-grow: 1;
    flex-shrink: 1;
`;

const ShortcutContainer = styled.span`
    display: flex;
    align-items: center;
    margin-inline-end: 0.25rem;
`;

const IconicLetter = styled.span`
    aspect-ratio: 1 / 1;
    width: 0.8em;
    text-align: center;
    font-weight: 800;
    line-height: 1;
`;

function Shortcut(props: { shortcut?: `shift+${string}` }) {
    const { shortcut } = props;
    if (!shortcut) {
        return null;
    }

    const [, key] = shortcut.split("+");
    return (
        <ShortcutContainer>
            <Icon name="shift" size="18" />
            <IconicLetter>{key.toUpperCase()}</IconicLetter>
        </ShortcutContainer>
    );
}

const Button = styled.button`
    all: unset;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.375rem;
    box-sizing: border-box;
    font-size: 0.8125rem;
    line-height: ${20 / 13};
    gap: 0.325rem;

    border-radius: 0.25rem;
    background-color: transparent;

    &:hover {
        background-color: var(--formant-color-primary-steel01);
    }

    &:focus-within {
        background-color: var(--color-background-active2);
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

interface IItemProps {
    onClick?: MouseEventHandler;
    icon?: IconName;
    shortcut?: `shift+${string}`;
    children: ReactNode;
    disabled?: boolean;
    launcher?: boolean;
    className?: string;
    textClassName?: string;
    "data-testid"?: string;
}

export function Item(props: IItemProps) {
    const {
        onClick,
        children,
        icon,
        shortcut,
        launcher,
        disabled,
        className,
        textClassName,
    } = props;
    const disableButton = disabled || !onClick;
    return (
        <Button
            onClick={onClick}
            className={className}
            disabled={disableButton}
            data-testid={`${children}-module-button`}
        >
            {icon && <Icon name={icon} size="18" />}
            <Text className={textClassName}>
                {children}
                {launcher && "…"}
            </Text>

            <Shortcut shortcut={shortcut} />
        </Button>
    );
}

Item.displayName = "Menu.Item";
